import { useMemo } from "react";

import { Permission } from "@/Common/Api/Employee/Dto/Permission";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { ProtectMedicalNoteComponent } from "../Allowlist/ProtectMedicalNoteComponent";
import { CollapsiblePermissionComponent } from "./CollapsiblePermisionComponent";
import { IPermissionSettingsComponentContext, PermissionCheckBoxComponent, PermissionSettingsComponentContext } from "./PermissionCheckboxComponent";

import styles from "./PermissionSettingsSectionComponent.module.scss";

interface PermissionSettingsSectionComponentProps {
    disabled: boolean;
    onPermissionCheckChange: (isChecked: boolean, permission: Permission[]) => void;
    permissionIds: string[];
    onAllowlistChange?: (model: IAllowlistModel) => void;
    allowlist?: IAllowlistModel;
    allowlistChange?: IAllowlistModel;
    containerClassName?: string;
}

function CheckBoxComponent(props: Readonly<{ permission: EnumMyleLoginPermission, description?: string }>): JSX.Element {
    const permission: Permission = useMemo(() => Permission.GetByEnum(props.permission), [props.permission]);
    if (!permission)
        return null;
    return (
        <div id={permission.Name} className={styles.indentation} data-cy={`permission-${permission.Name}`}>
            <PermissionCheckBoxComponent permission={permission} permissionDescription={props.description} />
        </div>
    );
}

function PermissionSettingsSectionComponent(props: Readonly<PermissionSettingsSectionComponentProps>): JSX.Element {
    const { getText } = useTranslation();

    const context: IPermissionSettingsComponentContext = useMemo(
        () => ({
            disabled: props.disabled,
            onPermissionCheckChange: props.onPermissionCheckChange,
            permissionIds: props.permissionIds }),
        [props.permissionIds, props.onPermissionCheckChange, props.disabled]
    );

    const baseDataCy = "permission";

    return (
        <PermissionSettingsComponentContext.Provider value={context}>
            <div className={props.containerClassName} data-cy={baseDataCy}>
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.AdminTabPermission} dataCy={baseDataCy}>
                    {cacheStore.ClinicSettings.EnableUserSettingsUiImprovements && (
                        <>
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_SiteTabPermission} />
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_ServiceCombosTabPermission} />
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_UsersTabPermission} />
                            {cacheStore.ClinicSettings.EnableRoleBasedAccessControl &&
                                <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_RolesTabPermission} />
                            }
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_BillableServicesTabPermission} />
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_CommunicationGroupsTabPermission} />
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_QuestionnairesTabPermission} />
                            <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_LogsTabPermission} />
                            {cacheStore.ClinicSettings.EnableUserPatientAutoMergeRequest &&
                                <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_PatientMergeRequestPermission} />
                            }
                        </>
                    )}
                    {(cacheStore.ClinicSettings.EnableExternalCommunicationV2) &&
                        <CheckBoxComponent permission={EnumMyleLoginPermission.Administration_ExternalConsultationTabPermission} />
                    }
                </CollapsiblePermissionComponent>
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.CalendarTabPermission} dataCy={baseDataCy} />
                {(!cacheStore.ClinicSettings?.EnablePatientEdit || cacheStore.ClinicSettings?.EnableCanCreatePatientPermission) &&
                (
                    <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.CanCreateAndEditPatientPermission} dataCy={baseDataCy} />
                )
                }
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.PatientTabPermission} dataCy={baseDataCy}>
                    {(cacheStore.ClinicSettings?.EnableMedicalNoteProtection
                        && props.allowlist != null
                        && props.permissionIds.some(employeePermission =>
                            employeePermission == Permission.GetByEnum(EnumMyleLoginPermission.PatientTabPermission).Id
                        )
                    ) && (
                        <ProtectMedicalNoteComponent
                            onChange={model => props.onAllowlistChange?.(model)}
                            model={props.allowlist}
                            modelChanges={props.allowlistChange}
                            dataCy="protectMedicalNoteComponent"
                            editable={!props.disabled}
                        />
                    )
                    }
                </CollapsiblePermissionComponent>
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.CommunicationsTabPermission} dataCy={baseDataCy} />
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.DocumentTabPermission} dataCy={baseDataCy}>
                    {cacheStore.hasInboundFaxPermission() && (
                        <CheckBoxComponent permission={EnumMyleLoginPermission.InboundFaxPermission} />
                    )}
                </CollapsiblePermissionComponent>
                {cacheStore.ClinicSettings.EnablePharmacyOrders && (
                    <CollapsiblePermissionComponent
                        permissionEnum={EnumMyleLoginPermission.OrdersTabPermission}
                        dataCy={baseDataCy}
                    />
                )}
                {cacheStore.ClinicSettings?.EnablePrescriptionRenewal && (
                    <CollapsiblePermissionComponent
                        permissionEnum={EnumMyleLoginPermission.PrescriptionRenewalTabPermission}
                        dataCy={baseDataCy}
                    />
                )}
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly} dataCy={baseDataCy}>
                    {cacheStore.ClinicSettings.EnableBillingReconciliationReport && (
                        <>
                            <CheckBoxComponent permission={EnumMyleLoginPermission.PrivateBillingPermission} />
                            <CheckBoxComponent permission={EnumMyleLoginPermission.PublicBillingPermission} />
                        </>
                    )}
                    <CheckBoxComponent permission={EnumMyleLoginPermission.BillingTabPermission} description={getText("RoleBasedPermissions_BillingTabPermissionDescription")} />

                    <CheckBoxComponent permission={EnumMyleLoginPermission.BillingCostPermission} />
                </CollapsiblePermissionComponent>
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.AffectationTabPermission} dataCy={baseDataCy} />
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.LabsTabPermission} dataCy={baseDataCy} />
                <CollapsiblePermissionComponent permissionEnum={EnumMyleLoginPermission.ReportingTabPermission} dataCy={baseDataCy} />
            </div>
        </PermissionSettingsComponentContext.Provider>
    );
}

export { PermissionSettingsSectionComponent };
